import { Injectable, computed, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, shareReplay, tap, throwError } from 'rxjs';
import { Permiso, RespuestaPermisos } from '../model/permisos';
import { secureStorage } from './localstorage.service';
import config from '../../../../../config/config';

// Única fuente de verdad para la cuenta interna sin restricción de permisos.
// El backend aísla por tenant_id, así que esta cuenta no puede leer datos reales
// de módulos aunque el frontend le abra las rutas; aun así se centraliza aquí
// para no tener la misma comparación duplicada en guard/directiva/app.component.
export function esCuentaSinRestriccion(email: string): boolean {
  return email === 'ipsimplementacion@gmail.com';
}

type AccionPermiso = 'bln_ver' | 'bln_editar' | 'bln_crear' | 'bln_eliminar';

@Injectable({
  providedIn: 'root'
})
export class Permisosv1Service {
  private readonly STORAGE_KEY = 'permisosv1';
  private readonly URL_API_PERMISOS = config.URL_API_BASE + '/permisos/v2/byIdUser';
  private readonly URL_API_PERMISOS_FALLBACK = config.URL_API_BASE + '/permisos/byIdUser';

  private readonly _permisos = signal<Permiso[]>([]);
  // true cuando ya hay una lista con la cual decidir (la de sessionStorage o la del backend).
  // Lo usa la directiva para no ocultar menús de forma definitiva antes de tener datos.
  private readonly _hayDatos = signal<boolean>(false);

  readonly permisos = this._permisos.asReadonly();
  readonly hayDatos = this._hayDatos.asReadonly();
  // Mapa path -> permiso para que tienePermiso/existeMenuRegistrado no recorran la lista cada vez.
  private readonly porPath = computed(() => {
    const mapa = new Map<string, Permiso>();
    for (const p of this._permisos()) {
      mapa.set(p.menus.str_menu_path, p);
    }
    return mapa;
  });

  // Carga del backend compartida durante la vida de la app: todos los que la piden
  // mientras está en vuelo (guard, app.component, login) reciben la MISMA petición.
  // La copia de sessionStorage solo sirve para pintar rápido; el guard siempre espera
  // esta carga porque sessionStorage lo puede editar el usuario.
  private cargaBackend$: Observable<Permiso[]> | null = null;

  constructor(private readonly http: HttpClient) {
    const stored = secureStorage.getSessionItem(this.STORAGE_KEY);
    if (Array.isArray(stored)) {
      this._permisos.set(stored);
      this._hayDatos.set(true);
    }
  }

  // Devuelve la carga del backend en curso o ya resuelta; solo hace una petición nueva
  // si no hay ninguna o si se pide forzar (login, cambio de contexto, edición de permisos).
  cargar$(idUsuarioSistemaCentro: number, forzar = false): Observable<Permiso[]> {
    if (this.cargaBackend$ && !forzar) {
      return this.cargaBackend$;
    }

    const carga$ = this.http.get<RespuestaPermisos>(`${this.URL_API_PERMISOS}/${idUsuarioSistemaCentro}`).pipe(
      catchError((error) => {
        console.warn('Permisos V2 failed, attempting fallback to V1', error);
        return this.http.get<RespuestaPermisos>(`${this.URL_API_PERMISOS_FALLBACK}/${idUsuarioSistemaCentro}`);
      }),
      map((res) => {
        if (!res?.status) {
          throw new Error(res?.message || 'No se pudieron obtener los permisos');
        }
        return res.body ?? [];
      }),
      tap((permisos) => this.setPermisos(permisos)),
      catchError((error) => {
        // Una carga fallida no se queda cacheada: el siguiente intento vuelve a consultar.
        if (this.cargaBackend$ === carga$) {
          this.cargaBackend$ = null;
        }
        return throwError(() => error);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.cargaBackend$ = carga$;
    return carga$;
  }

  // Versión "dispara y olvida" para quien no necesita esperar el resultado.
  recargar(idUsuarioSistemaCentro: number): void {
    this.cargar$(idUsuarioSistemaCentro, true).subscribe({ error: () => { } });
  }

  setPermisos(permisos: Permiso[]) {
    this._permisos.set(permisos);
    this._hayDatos.set(true);
    try {
      secureStorage.setSessionItem(this.STORAGE_KEY, JSON.stringify(permisos));
    } catch (e) {
      console.error('Error guardando permisos en sessionStorage', e);
    }
  }

  clearPermisos() {
    this._permisos.set([]);
    this._hayDatos.set(false);
    this.cargaBackend$ = null;
    secureStorage.removeSessionItem(this.STORAGE_KEY);
  }

  getPermisos(): Permiso[] {
    return this._permisos();
  }

  tienePermiso(ruta: string, accion: AccionPermiso): boolean {
    if (esCuentaSinRestriccion(secureStorage.obtenerEmail())) {
      return true;
    }
    const permiso = this.porPath().get(ruta);
    return permiso ? permiso[accion] : false;
  }

  // true si la ruta tiene un menú registrado en el catálogo de permisos del usuario,
  // sin importar si el permiso concedido es true o false.
  existeMenuRegistrado(ruta: string): boolean {
    return this.porPath().has(ruta);
  }

}
